import { useEffect, useState } from "react";
import {
  X,
  Minus,
  ImageSquare,
  Sparkle,
  CircleNotch,
  Copy,
  ArrowClockwise,
  Play,
  FloppyDisk,
  CheckSquare,
  Square,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  generateScenes,
  generateImageForScene,
  regenerateImage,
  updateScene,
  getArticle,
  imageUrl,
  errMessage,
} from "@/lib/api";

/**
 * Floating processing modal. When minimized, becomes a small chip at bottom-right.
 * - Checkbox next to each scene (all selected by default).
 * - Prompt textarea editable per scene, "Kaydet" button appears when dirty.
 * - "Görselleri Üret" only runs for CHECKED scenes that don't have image yet.
 */
export const ScenesModal = ({ articleId, onClose, onArticleUpdated }) => {
  const [article, setArticle] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);
  const [genScenes, setGenScenes] = useState(false);
  const [imgProgress, setImgProgress] = useState(null);
  const [regenSceneId, setRegenSceneId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingInit(true);
      try {
        const a = await getArticle(articleId);
        if (cancelled) return;
        setArticle(a);
        setSelected(new Set((a.scenes || []).map((s) => s.id)));
        if (!a.scenes || a.scenes.length === 0) {
          setGenScenes(true);
          const updated = await generateScenes(articleId);
          if (cancelled) return;
          setArticle(updated);
          setSelected(new Set((updated.scenes || []).map((s) => s.id)));
          onArticleUpdated(updated);
          toast.success(`${updated.scenes.length} sahne oluşturuldu`);
        }
      } catch (e) {
        if (!cancelled) toast.error(errMessage(e, "Sahne üretimi başarısız"));
      } finally {
        if (!cancelled) {
          setGenScenes(false);
          setLoadingInit(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [articleId]);

  const toggleSelected = (sceneId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set((article?.scenes || []).map((s) => s.id)));
  const selectNone = () => setSelected(new Set());

  const runGenerateImages = async () => {
    if (!article) return;
    const targets = article.scenes.filter((s) => selected.has(s.id) && !s.image_url);
    if (targets.length === 0) {
      toast.info("Seçili sahnelerin hepsi zaten üretilmiş");
      return;
    }
    setImgProgress({ current: 0, total: targets.length, ok: 0, failed: 0 });
    let latest = article;
    for (let i = 0; i < targets.length; i++) {
      const scene = targets[i];
      try {
        latest = await generateImageForScene(articleId, scene.id);
        setArticle(latest);
        onArticleUpdated(latest);
        setImgProgress((p) => ({ ...p, current: i + 1, ok: (p?.ok || 0) + 1 }));
      } catch (e) {
        setImgProgress((p) => ({ ...p, current: i + 1, failed: (p?.failed || 0) + 1 }));
        toast.error(errMessage(e, `Sahne #${i + 1} görseli üretilemedi`));
        if (e?.response?.status === 402 || e?.response?.status === 401) break;
      }
    }
    setImgProgress(null);
    const withImg = latest.scenes.filter((s) => s.image_url).length;
    toast.success(`${withImg}/${latest.scenes.length} görsel hazır`);
  };

  const runRegenScenes = async () => {
    setGenScenes(true);
    try {
      const updated = await generateScenes(articleId);
      setArticle(updated);
      setSelected(new Set((updated.scenes || []).map((s) => s.id)));
      onArticleUpdated(updated);
      toast.success(`${updated.scenes.length} sahne yeniden oluşturuldu`);
    } catch (e) {
      toast.error(errMessage(e, "Yeniden üretim başarısız"));
    } finally {
      setGenScenes(false);
    }
  };

  const runRegenImage = async (sceneId) => {
    setRegenSceneId(sceneId);
    try {
      const updated = await regenerateImage(articleId, sceneId);
      setArticle(updated);
      onArticleUpdated(updated);
      toast.success("Görsel yeniden üretildi");
    } catch (e) {
      toast.error(errMessage(e, "Yeniden üretim başarısız"));
    } finally {
      setRegenSceneId(null);
    }
  };

  const onSavePrompt = async (sceneId, newPrompt) => {
    try {
      const updated = await updateScene(articleId, sceneId, { image_prompt: newPrompt });
      setArticle(updated);
      onArticleUpdated(updated);
      toast.success("Prompt kaydedildi");
    } catch (e) {
      toast.error(errMessage(e, "Kaydetme başarısız"));
    }
  };

  const scenes = article?.scenes || [];
  const scenesCount = scenes.length;
  const imagesCount = scenes.filter((s) => s.image_url).length;
  const selectedCount = selected.size;
  const selectedMissing = scenes.filter((s) => selected.has(s.id) && !s.image_url).length;
  const genImages = !!imgProgress;

  if (minimized) {
    return (
      <button
        data-testid="scenes-modal-minimized"
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-50 h-12 pl-3 pr-4 border border-primary bg-[#0F0F0F] hover:bg-[#181818] flex items-center gap-3 shadow-2xl transition-colors animate-fade-in-up"
      >
        <div className="w-6 h-6 bg-primary flex items-center justify-center">
          {genScenes || genImages ? (
            <CircleNotch size={14} color="#fff" className="animate-spin" />
          ) : (
            <ImageSquare size={14} weight="fill" color="#fff" />
          )}
        </div>
        <div className="text-left">
          <p className="label-mono text-white leading-tight">İşleme</p>
          <p className="font-mono text-[11px] text-muted-foreground leading-tight tabular-nums">
            {scenesCount} sahne · {imagesCount}/{scenesCount} görsel
          </p>
        </div>
      </button>
    );
  }

  return (
    <div
      data-testid="scenes-modal"
      className="fixed bottom-4 right-4 z-50 w-[760px] max-w-[calc(100vw-2rem)] h-[calc(100vh-6rem)] max-h-[820px] border border-border bg-[#0A0A0A] shadow-2xl flex flex-col animate-fade-in-up"
    >
      <div className="h-12 border-b border-border flex items-center justify-between px-4 bg-[#0F0F0F]">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-primary flex items-center justify-center">
            <ImageSquare size={13} weight="fill" color="#fff" />
          </div>
          <div className="flex items-baseline gap-3">
            <span className="label-mono text-white">// processing</span>
            {article && (
              <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
                {scenesCount} sahne · {imagesCount}/{scenesCount} görsel · {selectedCount} seçili
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="scenes-modal-minimize"
            onClick={() => setMinimized(true)}
            className="h-8 w-8 flex items-center justify-center border border-border hover:border-white text-muted-foreground hover:text-white transition-colors"
            title="Küçült"
          >
            <Minus size={14} />
          </button>
          <button
            data-testid="scenes-modal-close"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center border border-border hover:border-primary hover:text-primary text-muted-foreground transition-colors"
            title="Kapat"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {scenes.length > 0 && !loadingInit && (
        <div className="border-b border-border px-4 py-2 flex items-center gap-3 bg-[#0A0A0A]">
          <button
            data-testid="scenes-select-all"
            onClick={selectAll}
            className="label-mono text-muted-foreground hover:text-primary"
          >
            Tümünü Seç
          </button>
          <span className="text-muted-foreground">·</span>
          <button
            data-testid="scenes-select-none"
            onClick={selectNone}
            className="label-mono text-muted-foreground hover:text-primary"
          >
            Hiçbirini Seçme
          </button>
          <div className="ml-auto label-mono text-muted-foreground">
            {selectedMissing} görsel üretilecek
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {loadingInit || (genScenes && scenes.length === 0) ? (
          <LoadingState label="Sahneler ve görsel prompt'ları hazırlanıyor…" />
        ) : scenes.length === 0 ? (
          <EmptyState onGenerate={runRegenScenes} loading={genScenes} />
        ) : (
          <ul className="divide-y divide-border">
            {scenes.map((s, idx) => (
              <SceneRow
                key={s.id}
                scene={s}
                idx={idx}
                checked={selected.has(s.id)}
                onToggle={() => toggleSelected(s.id)}
                onRegenImage={() => runRegenImage(s.id)}
                regenBusy={regenSceneId === s.id}
                onSavePrompt={onSavePrompt}
              />
            ))}
          </ul>
        )}
      </div>

      {scenes.length > 0 && (
        <div className="border-t border-border p-3 flex items-center justify-between gap-2 bg-[#0F0F0F]">
          <Button
            data-testid="scenes-regen-btn"
            variant="ghost"
            onClick={runRegenScenes}
            disabled={genScenes || genImages}
            className="rounded-none h-10 text-muted-foreground hover:text-white hover:bg-[#181818] label-mono"
          >
            <ArrowClockwise size={13} className="mr-2" />
            Sahneleri Yeniden Üret
          </Button>
          <Button
            data-testid="scenes-generate-images-btn"
            onClick={runGenerateImages}
            disabled={genImages || genScenes || selectedMissing === 0}
            className="rounded-none h-10 bg-primary hover:bg-[#D63028] text-white font-bold uppercase tracking-wider text-xs px-6 disabled:bg-[#1A1A1A] disabled:text-muted-foreground"
          >
            {genImages ? (
              <>
                <CircleNotch size={14} className="mr-2 animate-spin" />
                Üretiliyor… ({imgProgress?.current || 0}/{imgProgress?.total || 0})
              </>
            ) : (
              <>
                <Sparkle size={14} weight="fill" className="mr-2" />
                {selectedMissing} Görsel Üret
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

const SceneRow = ({ scene, idx, checked, onToggle, onRegenImage, regenBusy, onSavePrompt }) => {
  const [copied, setCopied] = useState(false);
  const [promptText, setPromptText] = useState(scene.image_prompt);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const dirty = promptText !== scene.image_prompt;

  useEffect(() => {
    setPromptText(scene.image_prompt);
  }, [scene.id, scene.image_prompt]);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await onSavePrompt(scene.id, promptText);
    } finally {
      setSavingPrompt(false);
    }
  };

  return (
    <li
      data-testid={`scene-row-${scene.id}`}
      className={`p-4 hover:bg-[#0D0D0D] transition-colors ${checked ? "" : "opacity-50"}`}
    >
      <div className="flex items-start gap-3">
        <button
          data-testid={`scene-check-${scene.id}`}
          onClick={onToggle}
          className="mt-1 shrink-0 text-muted-foreground hover:text-primary transition-colors"
          aria-label="toggle scene"
        >
          {checked ? (
            <CheckSquare size={18} weight="fill" color="#FF3B30" />
          ) : (
            <Square size={18} />
          )}
        </button>
        <span className="label-mono text-primary shrink-0 pt-1">
          #{String(idx + 1).padStart(2, "0")}
        </span>
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <span className="label-mono text-muted-foreground">// sahne açıklaması</span>
            <p className="text-sm text-white mt-1 leading-relaxed">{scene.text_summary}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="label-mono text-muted-foreground">// gemini prompt (EN)</span>
              <button
                data-testid={`scene-copy-${scene.id}`}
                onClick={copyPrompt}
                className="h-5 px-1.5 flex items-center gap-1 border border-border hover:border-primary hover:text-primary text-muted-foreground text-[9px] uppercase tracking-wider"
              >
                <Copy size={9} />
                {copied ? "Kopyalandı" : "Kopyala"}
              </button>
              {dirty && (
                <button
                  data-testid={`scene-save-prompt-${scene.id}`}
                  onClick={savePrompt}
                  disabled={savingPrompt}
                  className="h-5 px-1.5 flex items-center gap-1 border border-primary bg-primary text-white text-[9px] uppercase tracking-wider hover:bg-[#D63028]"
                >
                  <FloppyDisk size={9} weight="fill" />
                  {savingPrompt ? "Kaydediliyor" : "Kaydet"}
                </button>
              )}
            </div>
            <Textarea
              data-testid={`scene-prompt-${scene.id}`}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              className="font-mono text-[12px] leading-relaxed bg-[#0D0D0D] border border-border rounded-none min-h-[60px] focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0 p-2"
            />
          </div>

          <div>
            {scene.image_url ? (
              <div className="relative group">
                <img
                  data-testid={`scene-image-${scene.id}`}
                  src={imageUrl(scene.image_url)}
                  alt={scene.text_summary}
                  className="w-full max-w-md border border-border"
                />
                <button
                  data-testid={`scene-regen-image-${scene.id}`}
                  onClick={onRegenImage}
                  disabled={regenBusy}
                  className="absolute top-2 right-2 h-8 px-2 flex items-center gap-1 border border-border bg-[#0A0A0A]/90 hover:border-primary hover:text-primary text-white text-[10px] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {regenBusy ? (
                    <CircleNotch size={11} className="animate-spin" />
                  ) : (
                    <ArrowClockwise size={11} />
                  )}
                  Yeniden Üret
                </button>
              </div>
            ) : (
              <div className="border border-dashed border-border w-full max-w-md aspect-video flex items-center justify-center bg-[#0D0D0D]">
                <span className="label-mono text-muted-foreground">
                  {regenBusy ? "üretiliyor…" : checked ? "görsel henüz üretilmedi" : "seçili değil"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

const LoadingState = ({ label }) => (
  <div className="h-full flex flex-col items-center justify-center p-10 text-center">
    <CircleNotch size={28} color="#FF3B30" className="animate-spin mb-4" />
    <p className="label-mono text-muted-foreground">{label}</p>
    <p className="text-xs text-muted-foreground mt-2">
      Bu işlem 20-40 saniye sürebilir. Modalı küçültüp diğer işlere devam edebilirsin.
    </p>
  </div>
);

const EmptyState = ({ onGenerate, loading }) => (
  <div className="h-full flex flex-col items-center justify-center p-10 text-center">
    <ImageSquare size={32} color="#737373" className="mb-4" />
    <p className="label-mono text-muted-foreground mb-4">sahne bulunamadı</p>
    <Button
      onClick={onGenerate}
      disabled={loading}
      className="rounded-none h-10 bg-primary hover:bg-[#D63028] text-white font-bold uppercase tracking-wider text-xs"
    >
      {loading ? (
        <>
          <CircleNotch size={14} className="mr-2 animate-spin" />
          Üretiliyor…
        </>
      ) : (
        <>
          <Play size={14} weight="fill" className="mr-2" />
          Sahneleri Üret
        </>
      )}
    </Button>
  </div>
);

export default ScenesModal;
